import { useState } from 'react';
import { Badge } from '@london-life/ui/components/badge';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { ConfirmDialog } from '@london-life/ui/admin/confirm-dialog';
import { FormDialog } from '@london-life/ui/admin/form-dialog';
import { useAdmin } from '../../lib/admin-context';
import { when } from '../../lib/format';
import type { ContentEditor } from './use-content';

// What players see now, the draft being worked on, and every version before: save, publish (needs the publish
// permission and a fresh two-step code), throw the draft away, or bring an older version back.
export function ContentBar({ editor }: { editor: ContentEditor }) {
  const { can } = useAdmin();
  const { reply, dirty, problems, busy } = editor;
  const [publishing, setPublishing] = useState(false);
  const [note, setNote] = useState('');
  const [discarding, setDiscarding] = useState(false);
  const [history, setHistory] = useState(false);
  const [restoring, setRestoring] = useState<{ id: string; number: number } | null>(null);
  if (!reply) return null;
  const published = reply.published.version;
  const draft = reply.draft?.version ?? null;
  const canWrite = can('content.write');
  return (
    <div className="admin-panel content-bar" aria-label="Versions">
      <div className="content-bar-state">
        <span>
          Players see <strong>version {published.number}</strong>
          {published.publishedAt && <span className="admin-muted"> · published {when(published.publishedAt)}</span>}
        </span>
        {draft ? (
          <Badge variant="secondary">Draft started {when(draft.createdAt)}</Badge>
        ) : (
          <Badge variant="outline">No draft</Badge>
        )}
        {dirty && <Badge>Unsaved changes</Badge>}
        {problems.length > 0 ? (
          <Badge variant="destructive">
            {problems.length} problem{problems.length === 1 ? '' : 's'}
          </Badge>
        ) : (
          (draft || dirty) && <Badge variant="secondary">Ready to publish</Badge>
        )}
      </div>
      <div className="admin-page-actions wrap">
        {canWrite && (
          <>
            <Button type="button" onClick={() => void editor.saveDraft()} disabled={busy || !dirty}>
              Save draft
            </Button>
            <Button type="button" variant="outline" onClick={editor.undo} disabled={busy || !dirty}>
              Undo changes
            </Button>
          </>
        )}
        {can('content.publish') && (
          <Button
            type="button"
            variant="default"
            onClick={() => {
              setNote('');
              setPublishing(true);
            }}
            disabled={busy || problems.length > 0 || (!draft && !dirty)}
          >
            Publish
          </Button>
        )}
        {canWrite && draft && (
          <Button type="button" variant="outline" onClick={() => setDiscarding(true)} disabled={busy}>
            Throw away the draft
          </Button>
        )}
        <Button type="button" variant="ghost" onClick={() => setHistory(!history)} aria-expanded={history}>
          {history ? 'Hide history' : 'History'}
        </Button>
      </div>
      {problems.length > 0 && (
        <ul className="content-problems" aria-label="Problems to fix before publishing">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}
      {history && (
        <ol className="content-history" aria-label="Every version">
          {reply.history.map((version) => (
            <li key={version.id}>
              <span>
                <strong>Version {version.number}</strong>{' '}
                <Badge variant={version.status === 'published' ? 'default' : 'outline'}>{version.status}</Badge>
              </span>
              <span className="admin-muted">
                {version.note ?? 'No note'} · {when(version.publishedAt ?? version.createdAt)}
              </span>
              {version.status === 'retired' && can('content.publish') && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setRestoring({ id: version.id, number: version.number })}
                >
                  Bring back
                </Button>
              )}
            </li>
          ))}
        </ol>
      )}
      <FormDialog
        open={publishing}
        title="Publish to players"
        description="Players see these places and activities straight away (open games load them within seconds). The version players have now is kept, and can be brought back."
        submitLabel="Publish"
        onSubmit={async () => {
          if (!(await editor.publish(note.trim() || undefined)))
            throw new Error('Not published. See the message on the page.');
        }}
        onClose={() => setPublishing(false)}
      >
        <div className="admin-field">
          <Label htmlFor="publish-note">What changed (for the history)</Label>
          <Input id="publish-note" maxLength={200} value={note} onChange={(event) => setNote(event.target.value)} />
        </div>
      </FormDialog>
      <ConfirmDialog
        open={discarding}
        title="Throw away the draft"
        description="Every change in the draft is lost. Players keep the version they have."
        confirmLabel="Throw it away"
        danger
        onConfirm={async () => {
          await editor.discardDraft();
        }}
        onClose={() => setDiscarding(false)}
      />
      <ConfirmDialog
        open={!!restoring}
        title={restoring ? `Bring back version ${restoring.number}` : ''}
        description="It is published again as a new version, and players get it straight away. Nothing is lost: the version they have now stays in the history."
        confirmLabel="Bring it back"
        onConfirm={async () => {
          if (restoring) await editor.restore(restoring.id);
        }}
        onClose={() => setRestoring(null)}
      />
    </div>
  );
}
